import { Nullable } from "@core/domain/types/nullable.type";
import { ExpenseDto } from "@project/data/dto/expenses/expenses.dto";
import { ExpenseAllTypes } from "@project/domain/models/expenses/expense.model";
import { validateSync } from "class-validator";
import { inject, injectable } from "inversify";
import { ExpenseTypeEnumDto } from "../../dto/expenses/expense-type.dto";
import { InvestmentExpenseMapper } from "./investment-expense.mapper";
import { MaintenanceExpenseMapper } from "./maintenance-expense.mapper";
import { ManagementExpenseMapper } from "./management-expense.mapper";
import { OtherSubsidizableExpenseMapper } from "./otherSubsidizable-expense.mapper";
import { PersonnelExpenseMapper } from "./personnel-expense.mapper";
import { TravelExpenseMapper } from "./travel-expense.mapper";

@injectable()
export class ExpenseMapper {
    constructor(
        @inject(InvestmentExpenseMapper)
        private readonly investmentExpenseMapper: InvestmentExpenseMapper,
        @inject(MaintenanceExpenseMapper)
        private readonly maintenanceExpenseMapper: MaintenanceExpenseMapper,
        @inject(TravelExpenseMapper)
        private readonly travelExpenseMapper: TravelExpenseMapper,
        @inject(ManagementExpenseMapper)
        private readonly managementExpenseMapper: ManagementExpenseMapper,
        @inject(PersonnelExpenseMapper)
        private readonly personnelExpenseMapper: PersonnelExpenseMapper,
        @inject(OtherSubsidizableExpenseMapper)
        private readonly otherSubsidizableExpenseMapper: OtherSubsidizableExpenseMapper,
    ) {}
    map(expenseDto: ExpenseDto): Nullable<ExpenseAllTypes> {
        const errors = validateSync(expenseDto);

        if (errors.length > 0) {
            console.error(errors);

            return null;
        }

        let mapped: Nullable<ExpenseAllTypes> = null;

        switch (expenseDto.type_expense) {
            case ExpenseTypeEnumDto.INVESTMENT:
                if (
                    expenseDto.investment_data &&
                    expenseDto.investment_data.length > 0
                ) {
                    mapped = this.investmentExpenseMapper.map(
                        expenseDto,
                        expenseDto.investment_data[0],
                    );
                }
                break;
            case ExpenseTypeEnumDto.MAINTENANCE_ACTIVITIES:
                if (
                    expenseDto.investment_data &&
                    expenseDto.investment_data.length > 0
                ) {
                    mapped = this.maintenanceExpenseMapper.map(
                        expenseDto,
                        expenseDto.investment_data[0],
                    );
                }
                break;
            case ExpenseTypeEnumDto.MANAGEMENT_ADMINISTRATION:
                if (
                    expenseDto.investment_data &&
                    expenseDto.investment_data.length > 0
                ) {
                    mapped = this.managementExpenseMapper.map(
                        expenseDto,
                        expenseDto.investment_data[0],
                    );
                }
                break;
            case ExpenseTypeEnumDto.TRAVEL:
                if (
                    expenseDto.travel_data &&
                    expenseDto.travel_data.length > 0
                ) {
                    mapped = this.travelExpenseMapper.map(
                        expenseDto,
                        expenseDto.travel_data[0],
                    );
                }
                break;
            case ExpenseTypeEnumDto.ACCOMMODATION_MEALS:
                // ACCOMMODATION_MEALS ahora se maneja como subtipo de TRAVEL
                // Esta funcionalidad se movió al TravelExpenseMapper
                break;
            case ExpenseTypeEnumDto.PERSONNEL_MANAGEMENT_ADMINISTRATION:
                if (
                    expenseDto.management_data &&
                    expenseDto.management_data.length > 0
                ) {
                    mapped = this.personnelExpenseMapper.map(
                        expenseDto,
                        expenseDto.management_data[0],
                    );
                }
                break;
            case ExpenseTypeEnumDto.OTHER_SUBSIDIZABLE_EXPENSES:
                if (
                    expenseDto.other_subsidizable_data &&
                    expenseDto.other_subsidizable_data.length > 0
                ) {
                    mapped = this.otherSubsidizableExpenseMapper.map(
                        expenseDto,
                        expenseDto.other_subsidizable_data[0],
                    );
                }
                break;
            default:
                break;
        }
        if (!mapped) {
            // Fallback genérico si no hay datos específicos
            mapped = this.buildGenericExpense(expenseDto);
        }
        return mapped;
    }

    // Nuevo método helper para crear un gasto genérico si no hay datos específicos
    private buildGenericExpense(
        expenseDto: ExpenseDto,
    ): ExpenseAllTypes | null {
        // Reutilizamos la información básica para que aparezca en listados.
        return {
            id: expenseDto.id,
            projectId: expenseDto.project,
            type: expenseDto.type_expense as any,
            // No existe subtype_expense en ExpenseDto plano; si hace falta derivar se podría inferir de data vacía.
            subType: undefined as any,
            typeLabel: expenseDto.type_expense,
            subTypeLabel: "",
            amountImputation: 0,
        } as unknown as ExpenseAllTypes;
    }
}
